import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { getData } from '../Services/api';
import Pagination from '../Services/Pagination';

const POHistory = () => {
    const [orders, setOrders] = useState([]);
    const [page, setPage] = useState(1);
    const [pages, setPages] = useState(1);
    const [total, setTotal] = useState(0);
    const [perPage] = useState(50);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [loading, setLoading] = useState(false);

    // Default fromDate = 3 months ago
    const getDefaultFromDate = () => {
        const d = new Date();
        d.setMonth(d.getMonth() - 3);
        return d.toISOString().split('T')[0];
    };

    const fetchHistory = useCallback(async (pageNumber = page) => {
        setLoading(true);
        try {
            let url = `/proforma-orders/history?page=${pageNumber}&limit=${perPage}`;
            if (search.trim()) url += `&search=${encodeURIComponent(search.trim())}`;
            if (statusFilter && statusFilter !== 'all') url += `&status=${statusFilter}`;
            if (fromDate) url += `&from=${fromDate}`;
            if (toDate) url += `&to=${toDate}`;

            const res = await getData(url);
            if (res.status) {
                setOrders(res.data || []);
                setPage(res.pagination?.page || 1);
                setPages(res.pagination?.totalPages || 1);
                setTotal(res.pagination?.total || 0);
            } else {
                setOrders([]);
            }
        } catch (error) {
            console.error("Error fetching PO history:", error);
            setOrders([]);
        } finally {
            setLoading(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, perPage, search, statusFilter, fromDate, toDate]);

    useEffect(() => {
        fetchHistory(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, search, statusFilter, fromDate, toDate]);

    const handlePageChange = (pageNumber) => {
        if (pageNumber !== page) {
            setPage(pageNumber);
        }
    };

    const handleReset = () => {
        setSearch('');
        setStatusFilter('all');
        setFromDate('');
        setToDate('');
        setPage(1);
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'invoiced':   return <span className="badge bg-success">INVOICED</span>;
            case 'cancelled':  return <span className="badge bg-danger">CANCELLED</span>;
            case 'checking':   return <span className="badge bg-warning text-dark">CHECKING</span>;
            case 'ready_for_delivery': return <span className="badge bg-primary">READY FOR DELIVERY</span>;
            default:           return <span className="badge bg-secondary">{status?.toUpperCase()}</span>;
        }
    };

    return (
        <div className="pc-container">
            <div className="pc-content">
                {/* Page Header */}
                <div className="page-header">
                    <div className="page-block">
                        <div className="row align-items-center">
                            <div className="col-md-12">
                                <ul className="breadcrumb">
                                    <li className="breadcrumb-item"><Link to="/">Home</Link></li>
                                    <li className="breadcrumb-item"><Link to="/accounts-dashboard/history">Accounts</Link></li>
                                    <li className="breadcrumb-item" aria-current="page">PO History</li>
                                </ul>
                            </div>
                            <div className="col-md-12">
                                <div className="page-header-title">
                                    <h2 className="mb-0">Proforma Order History</h2>
                                    <p className="text-muted mb-0 mt-1">Past POs from the last 3 months — invoiced &amp; cancelled</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="row">
                    <div className="col-sm-12">
                        <div className="card">
                            <div className="card-body">
                                {/* Filters Row */}
                                <div className="p-4">
                                    <div className="row g-3 align-items-end">
                                        {/* Search */}
                                        <div className="col-md-3">
                                            <label className="form-label fw-semibold">Search</label>
                                            <div className="form-search">
                                                <i className="fas fa-search text-muted" />
                                                <input
                                                    type="search"
                                                    className="form-control"
                                                    placeholder="Customer or Username..."
                                                    value={search}
                                                    onChange={(e) => {
                                                        setSearch(e.target.value);
                                                        setPage(1);
                                                    }}
                                                />
                                            </div>
                                        </div>

                                        {/* Status Filter */}
                                        <div className="col-md-2">
                                            <label className="form-label fw-semibold">Status</label>
                                            <select
                                                className="form-select"
                                                value={statusFilter}
                                                onChange={(e) => {
                                                    setStatusFilter(e.target.value);
                                                    setPage(1);
                                                }}
                                            >
                                                <option value="all">All History</option>
                                                <option value="invoiced">Invoiced</option>
                                                <option value="cancelled">Cancelled</option>
                                            </select>
                                        </div>

                                        {/* From Date */}
                                        <div className="col-md-2">
                                            <label className="form-label fw-semibold">From Date</label>
                                            <input
                                                type="date"
                                                className="form-control"
                                                value={fromDate}
                                                placeholder={getDefaultFromDate()}
                                                onChange={(e) => {
                                                    setFromDate(e.target.value);
                                                    setPage(1);
                                                }}
                                            />
                                        </div>

                                        {/* To Date */}
                                        <div className="col-md-2">
                                            <label className="form-label fw-semibold">To Date</label>
                                            <input
                                                type="date"
                                                className="form-control"
                                                value={toDate}
                                                onChange={(e) => {
                                                    setToDate(e.target.value);
                                                    setPage(1);
                                                }}
                                            />
                                        </div>

                                        {/* Reset Button */}
                                        <div className="col-md-1">
                                            <button
                                                className="btn btn-outline-secondary w-100"
                                                onClick={handleReset}
                                                title="Reset Filters"
                                            >
                                                <i className="fas fa-undo" /> Reset
                                            </button>
                                        </div>

                                        {/* Total Badge */}
                                        <div className="col-md-2 text-end">
                                            <span className="badge bg-primary fs-6">
                                                Total: {total}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Table */}
                                <div className="table-responsive px-2">
                                    <table className="table table-hover" id="po-history-table">
                                        <thead className="table-light">
                                            <tr>
                                                <th>S.No.</th>
                                                <th>PO Number</th>
                                                <th>PO Type</th>
                                                <th>Customer Name</th>
                                                <th>Username</th>
                                                <th>Date</th>
                                                <th>Total Amount</th>
                                                <th className="text-center">Status</th>
                                                <th>Invoice No.</th>
                                                <th className="text-center">Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {loading ? (
                                                <tr>
                                                    <td colSpan={10} className="text-center py-5">
                                                        <div className="spinner-border text-primary" role="status">
                                                            <span className="visually-hidden">Loading...</span>
                                                        </div>
                                                        <div className="mt-2 text-muted">Loading history...</div>
                                                    </td>
                                                </tr>
                                            ) : orders.length > 0 ? orders.map((order, index) => (
                                                <tr key={order.id}>
                                                    <td>{index + 1 + (page - 1) * perPage}</td>
                                                    <td className="fw-semibold text-primary">{order.po_number}</td>
                                                    <td>
                                                        <span className="badge bg-light text-dark border">{order.po_type || '-'}</span>
                                                    </td>
                                                    <td className="fw-bold">{order.customer_name}</td>
                                                    <td>
                                                        <span className="text-muted">
                                                            <i className="fas fa-user-tie me-1 text-secondary" />
                                                            {order.createdByUser?.username || '-'}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <span className="text-muted">{order.date}</span>
                                                        {order.time && <><br /><small className="text-muted">{order.time}</small></>}
                                                    </td>
                                                    <td className="fw-bold">
                                                        ₹{Number(order.total_amount).toLocaleString('en-IN')}
                                                    </td>
                                                    <td className="text-center">
                                                        {getStatusBadge(order.status)}
                                                    </td>
                                                    <td>
                                                        {order.invoice_number ? (
                                                            <span className="badge bg-success bg-opacity-10 text-success border border-success fw-semibold">
                                                                {order.invoice_number}
                                                            </span>
                                                        ) : (
                                                            <span className="text-muted opacity-50">—</span>
                                                        )}
                                                    </td>
                                                    <td className="text-center">
                                                        <Link 
                                                            to={`/accounts-dashboard/view-po/${order.id}`}
                                                            className="avtar avtar-xs btn-pc-default btn-link-secondary"
                                                            title="View PO Details"
                                                            style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', margin: 0 }}
                                                        >
                                                            <i className="fas fa-eye f-18" />
                                                        </Link>
                                                    </td>
                                                </tr>
                                            )) : (
                                                <tr>
                                                    <td className="text-center py-5 text-muted" colSpan={10}>
                                                        <i className="fas fa-history fs-3 mb-2 opacity-25 d-block" />
                                                        <strong>No PO History Found.</strong>
                                                        <div className="small mt-1">Try adjusting your search filters or date range.</div>
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Pagination */}
                                {!loading && orders.length > 0 && (
                                    <div className="d-flex justify-content-between align-items-center px-4 pb-3 mt-2">
                                        <div className="text-muted small">
                                            Showing {(page - 1) * perPage + 1} to {Math.min(page * perPage, total)} of {total} entries
                                        </div>
                                        <Pagination page={page} pages={pages} onPageChange={handlePageChange} />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default POHistory;
